import { useMemo, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { ProgressCard } from '@components/progress';
import { VirtualizedSectionProgressList } from '@components/progress';
import { BucketAnalytics } from './BucketAnalytics';
import { StorageService } from '@services';

const PRD_REVIEW_INTERVALS = [[30, 1], [50, 4], [70, 24], [85, 72], [95, 168], [100, 336]]; // [scoreThreshold, hours]

/**
 * Course Dashboard Component
 * Displays overall course progress, bucket analytics, and learning recommendations
 * Optimized with virtualized section list for large courses
 */
export function CourseDashboard({ course, progress, userId, onStartLearning, onStartQuestionPractice, onStartFlashcards, onStartReviewContent, onLoadSection }) {
  const [importMessage, setImportMessage] = useState(null); // { type: 'success'|'danger', text }
  const sections = useMemo(() => course?.sections || [], [course?.sections]);
  const sectionProgressById = useMemo(() => progress?.sectionProgress || {}, [progress?.sectionProgress]);
  const questionProgressById = useMemo(() => progress?.questionProgress || {}, [progress?.questionProgress]);
  const currentSection = sections.find((section) => section.id === progress?.currentSectionId);

  const handleExport = () => {
    StorageService.exportProgress(progress, userId, course.id);
  };

  const handleImport = () => {
    StorageService.importProgress((err, data) => {
      if (err) {
        setImportMessage({ type: 'danger', text: err.message });
        return;
      }
      // Basic validation
      if (!data?.progress || data.courseId !== course.id) {
        setImportMessage({ type: 'danger', text: 'This file does not match the current course.' });
        return;
      }
      setImportMessage({ type: 'success', text: 'Progress imported — please refresh the page to apply.' });
    });
  };

  // F10 (PRD): Study streak — consecutive calendar days with any activity
  const studyStreak = useMemo(() => {
    const ONE_DAY = 86400000;
    const studiedDays = new Set(
      Object.values(sectionProgressById)
        .flatMap((sp) => [sp.lastActivityAt, sp.firstStarted].filter(Boolean))
        .map((d) => {
          const date = new Date(d);
          date.setHours(0, 0, 0, 0);
          return date.getTime();
        }),
    );
    if (!studiedDays.size) return 0;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    let checkDay = todayStart.getTime();
    // If no activity today yet, still count the current streak starting from yesterday
    if (!studiedDays.has(checkDay)) checkDay -= ONE_DAY;
    let streak = 0;
    while (studiedDays.has(checkDay)) {
      streak++;
      checkDay -= ONE_DAY;
    }
    return streak;
  }, [sectionProgressById]);

  const sectionsDueForReview = useMemo(() => {
    if (typeof progress?.getSectionProgress !== 'function') return [];

    const nowMs = Date.now();
    return sections.filter((section) => {
      const sp = progress.getSectionProgress(section.id);
      if (!sp?.lastActivityAt || !sp.unlocked || (sp.timesStudied || 0) === 0) return false;
      const hoursSince = (nowMs - new Date(sp.lastActivityAt).getTime()) / 3600000;
      const score = sp.score || 0;
      let requiredHours = 1;
      for (const [threshold, interval] of PRD_REVIEW_INTERVALS) {
        if (score >= threshold) requiredHours = interval;
      }
      return hoursSince >= requiredHours;
    });
  }, [sections, progress]);

  if (!course || !progress) {
    return (
      <Container className="py-5">
        <Card>
          <Card.Body className="text-center py-5">
            <p>Loading course data...</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="course-dashboard py-4">
      <Row className="mb-4">
        <Col>
          <h2>{course.title}</h2>
          <p className="text-muted">{course.description}</p>
        </Col>
      </Row>

      <Row>
        <Col lg={8}>
          <Card className="mb-4">
            <Card.Body>
              <h4 className="mb-3">Continue Learning</h4>
              {currentSection ? (
                <>
                  <p>Current Section: <strong>{currentSection.title}</strong></p>
                  <p className="text-muted">{currentSection.description}</p>
                  <Button 
                    variant="primary" 
                    size="lg"
                    onClick={onStartLearning}
                  >
                    Start Learning →
                  </Button>
                  {onStartQuestionPractice && (
                    <Button 
                      variant="outline-primary" 
                      size="lg"
                      className="ms-2"
                      onClick={onStartQuestionPractice}
                    >
                      Practice Questions →
                    </Button>
                  )}
                  {onStartFlashcards && (
                    <Button 
                      variant="outline-secondary" 
                      size="lg"
                      className="ms-2"
                      onClick={onStartFlashcards}
                    >
                      Flashcards →
                    </Button>
                  )}
                  {onStartReviewContent && (
                    <Button 
                      variant="outline-dark" 
                      size="lg"
                      className="ms-2"
                      onClick={onStartReviewContent}
                    >
                      Review Content →
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <p>Ready to begin your adaptive learning journey!</p>
                  <Button 
                    variant="primary" 
                    size="lg"
                    onClick={onStartLearning}
                  >
                    Begin Course →
                  </Button>
                  {onStartQuestionPractice && (
                    <Button 
                      variant="outline-primary" 
                      size="lg"
                      className="ms-2"
                      onClick={onStartQuestionPractice}
                    >
                      Practice Questions →
                    </Button>
                  )}
                  {onStartFlashcards && (
                    <Button 
                      variant="outline-secondary" 
                      size="lg"
                      className="ms-2"
                      onClick={onStartFlashcards}
                    >
                      Flashcards →
                    </Button>
                  )}
                  {onStartReviewContent && (
                    <Button 
                      variant="outline-dark" 
                      size="lg"
                      className="ms-2"
                      onClick={onStartReviewContent}
                    >
                      Review Content →
                    </Button>
                  )}
                </>
              )}
            </Card.Body>
          </Card>

          {/* Virtualized Section Progress - Optimized for large courses */}
          <VirtualizedSectionProgressList 
            course={course} 
            progress={progress}
            onLoadSection={onLoadSection}
          />

          {/* Bucket Analytics */}
          {userId && course.id && (
            <Card className="mb-4">
              <Card.Header>
                <h6 className="mb-0">📊 Learning Analytics</h6>
              </Card.Header>
              <Card.Body>
                <BucketAnalytics 
                  userId={userId} 
                  courseId={course.id}
                />
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col lg={4}>
          <ProgressCard progress={progress} course={course} />

          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Quick Stats</h6>
            </Card.Header>
            <Card.Body>
              <div className="stat-row mb-3">
                {/* F10 (PRD): "Shows total questions answered" — user's answered count, not course total */}
                <span>Questions Answered</span>
                <strong>
                  {Object.keys(progress.questionProgress || {}).filter(qId => {
                    const qp = questionProgressById[qId];
                    return (qp.timesShown ?? qp.attempts ?? 0) > 0;
                  }).length}
                </strong>
              </div>
              <div className="stat-row mb-3">
                <span>Total Questions</span>
                <strong>
                  {sections.reduce((sum, s) => {
                    const sp = progress.getSectionProgress(s.id);
                    return sum + (sp?.questionsAttempted || 0);
                  }, 0)}
                </strong>
              </div>
              <div className="stat-row mb-3">
                <span>Sections Completed</span>
                <strong>
                  {sections.filter(s => progress.isSectionCompleted(s.id)).length} / {sections.length}
                </strong>
              </div>
              <div className="stat-row mb-3">
                <span>Study Streak</span>
                <strong>
                  {studyStreak > 0
                    ? <span className="text-success">🔥 {studyStreak} day{studyStreak !== 1 ? 's' : ''}</span>
                    : <span className="text-muted">No streak yet</span>}
                </strong>
              </div>
            </Card.Body>
          </Card>

          {sectionsDueForReview.length > 0 && (
            <Card className="mb-4 border-warning">
              <Card.Header className="bg-warning bg-opacity-10">
                <h6 className="mb-0">⏰ Due for Review ({sectionsDueForReview.length})</h6>
              </Card.Header>
              <Card.Body className="py-2">
                {sectionsDueForReview.map((section) => {
                  const sp = progress.getSectionProgress(section.id);
                  return (
                    <div key={section.id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                      <small className="text-truncate me-2">{section.title}</small>
                      <Badge bg="warning" text="dark" className="flex-shrink-0">
                        {Math.round(sp?.score || 0)}%
                      </Badge>
                    </div>
                  );
                })}
              </Card.Body>
            </Card>
          )}
          {/* F12 (PRD): Export / Import progress */}
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">⚙️ Data</h6>
            </Card.Header>
            <Card.Body>
              {importMessage && (
                <Alert
                  variant={importMessage.type}
                  dismissible
                  onClose={() => setImportMessage(null)}
                  className="mb-3"
                >
                  {importMessage.text}
                </Alert>
              )}
              <div className="d-grid gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleExport}
                  title="Download your progress as a JSON file"
                >
                  ⬇️ Export Progress
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleImport}
                  title="Restore progress from a previously exported JSON file"
                >
                  ⬆️ Import Progress
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

const sectionPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  description: PropTypes.string,
});

const sectionProgressEntryPropType = PropTypes.shape({
  lastActivityAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  firstStarted: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  unlocked: PropTypes.bool,
  timesStudied: PropTypes.number,
  score: PropTypes.number,
  questionsAttempted: PropTypes.number,
});

const questionProgressEntryPropType = PropTypes.shape({
  timesShown: PropTypes.number,
  attempts: PropTypes.number,
});

CourseDashboard.propTypes = {
  course: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    sections: PropTypes.arrayOf(sectionPropType),
  }),
  progress: PropTypes.shape({
    currentSectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sectionProgress: PropTypes.objectOf(sectionProgressEntryPropType),
    questionProgress: PropTypes.objectOf(questionProgressEntryPropType),
    getSectionProgress: PropTypes.func,
    isSectionCompleted: PropTypes.func,
  }),
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onStartLearning: PropTypes.func,
  onStartQuestionPractice: PropTypes.func,
  onStartFlashcards: PropTypes.func,
  onStartReviewContent: PropTypes.func,
  onLoadSection: PropTypes.func,
};
